import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import AuthLayout from '../components/AuthLayout';
import SignUpLeftVisual from '../components/auth/SignUpLeftVisual';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fadeInUp } from '../utils/animations';
import './AuthPages.css';
import './OTPVerification.css';

function OTPVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmSignUp, signin, resendConfirmationCode } = useAuth();
  const { toast } = useToast();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(180); // 3 minutes in seconds
  const [isExpired, setIsExpired] = useState(false);
  const inputRefs = useRef([]);

  // Start countdown timer when component mounts (OTP was sent during signup)
  // Timer restarts automatically when isExpired changes from true to false (after resend)
  useEffect(function () {
    // Don't run timer if expired and at 0 (will restart when resend resets isExpired)
    if (isExpired && timeRemaining === 0) {
      return;
    }

    const timer = setInterval(function () {
      setTimeRemaining(function (prev) {
        if (prev <= 1) {
          setIsExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return function () {
      clearInterval(timer);
    };
  }, [isExpired]); // Only restart when isExpired changes (when resend happens)

  useEffect(function () {
    const userName = location.state?.userName;
    const email = location.state?.email;
    const password = location.state?.password;
    
    // Check if data exists in location state or sessionStorage
    if (!userName || !email || !password) {
      const sessionData = sessionStorage.getItem('otp-signup-data');
      if (!sessionData) {
        // No data found, redirect to signup
        navigate('/signup', { replace: true });
        return;
      }
    } else {
      // Store in sessionStorage as backup
      sessionStorage.setItem('otp-signup-data', JSON.stringify({
        userName,
        email,
        password,
      }));
    }

    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [location.state, navigate]);

  function handleChange(index, value) {
    if (!/^\d*$/.test(value)) {
      return;
    }
    
    if (value.length > 1) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index, e) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).split('');
    const newOtp = [...otp];
    
    pastedData.forEach(function (char, i) {
      if (i < 6 && /^\d$/.test(char)) {
        newOtp[i] = char;
      }
    });
    
    setOtp(newOtp);
    const nextEmptyIndex = newOtp.findIndex(function (val) { return val === ''; });
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      return;
    }

    if (isExpired) {
      setError('OTP expired. Please request a new code.');
      return;
    }

    // Try to get data from location state first, then sessionStorage
    let userName = location.state?.userName;
    let email = location.state?.email;
    let password = location.state?.password;

    if (!userName || !email || !password) {
      const sessionData = sessionStorage.getItem('otp-signup-data');
      if (sessionData) {
        try {
          const parsed = JSON.parse(sessionData);
          userName = parsed.userName;
          email = parsed.email;
          password = parsed.password;
        } catch (err) {
          setError('Session expired. Please sign up again.');
          sessionStorage.removeItem('otp-signup-data');
          navigate('/signup', { replace: true });
          return;
        }
      } else {
        setError('Session expired. Please sign up again.');
        navigate('/signup', { replace: true });
        return;
      }
    }

    setError('');
    setLoading(true);

    try {
      // First, confirm the OTP code using username (which is now the Cognito username)
      await confirmSignUp(userName, otpValue);
      
      // Clear sessionStorage after successful verification
      sessionStorage.removeItem('otp-signup-data');
      toast('Email verified successfully! Please sign in to continue.', 'success');
      // After successful verification, redirect to sign in page
      navigate('/signin', { 
        state: { 
          message: 'Email verified successfully! Please sign in to continue.',
          email: email 
        } 
      });
    } catch (err) {
      const msg = err.message || 'Verification failed. Please check your code and try again.';
      setError(msg);
      toast(msg, 'error');
      setLoading(false);
    }
  }

  function formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }

  async function handleResend() {
    let userName = location.state?.userName;
    
    if (!userName) {
      const sessionData = sessionStorage.getItem('otp-signup-data');
      if (sessionData) {
        try {
          const parsed = JSON.parse(sessionData);
          userName = parsed.userName;
        } catch (err) {
          setError('Session expired. Please sign up again.');
          navigate('/signup', { replace: true });
          return;
        }
      } else {
        setError('Session expired. Please sign up again.');
        navigate('/signup', { replace: true });
        return;
      }
    }
    
    setResending(true);
    setResendSuccess(false);
    setError('');
    
    try {
      // Call Cognito to resend the confirmation code
      await resendConfirmationCode(userName);
      
      // Clear OTP input fields
      setOtp(['', '', '', '', '', '']);
      
      // Reset timer to 3 minutes and clear expired state
      setIsExpired(false);
      setTimeRemaining(180);
      
      // Show success message
      setResendSuccess(true);
      toast('Verification code resent to your email.', 'success');
      setTimeout(() => setResendSuccess(false), 3000);
      
      // Focus on first input after resend
      if (inputRefs.current[0]) {
        setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 100);
      }
    } catch (err) {
      const msg = err.message || 'Failed to resend code. Please try again.';
      setError(msg);
      toast(msg, 'error');
    } finally {
      setResending(false);
    }
  }

  const isComplete = otp.every(function (digit) { return digit !== ''; });

  return (
    <AuthLayout leftContent={<SignUpLeftVisual />}>
      <motion.div {...fadeInUp}>
        <Card className="ui-card--glass">
          <form className="auth-form" onSubmit={handleSubmit}>
            <p className="auth-form-welcome">Verify Your Email</p>
            <h2 className="auth-form-title">Enter OTP</h2>
            <p className="otp-subtitle">
              We've sent a 6-digit code to your email. Please enter it below.
            </p>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="auth-form-error"
                style={{ color: 'var(--error-color, #ff4444)', marginBottom: '1rem', fontSize: '0.875rem', textAlign:"center" }}
              >
                {error}
              </motion.div>
            )}

            <div className="otp-container" onPaste={handlePaste}>
              {otp.map(function (digit, index) {
                return (
                  <input
                    key={index}
                    ref={function (el) { inputRefs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="1"
                    className={'otp-input' + (digit ? ' otp-input--filled' : '')}
                    value={digit}
                    onChange={function (e) { handleChange(index, e.target.value); }}
                    onKeyDown={function (e) { handleKeyDown(index, e); }}
                    autoComplete="off"
                  />
                );
              })}
            </div>

            {/* OTP Timer */}
            {!isExpired ? (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="otp-timer"
              >
                <span className="otp-timer-text">
                  Code expires in <span className="otp-timer-value">{formatTime(timeRemaining)}</span>
                </span>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="otp-expired"
              >
                <span className="otp-expired-text">OTP expired</span>
              </motion.div>
            )}

            <motion.div
              whileHover={!isExpired ? { scale: 1.02 } : {}}
              whileTap={!isExpired ? { scale: 0.98 } : {}}
            >
              <Button
                type="submit"
                variant="primary"
                fullWidth
                className="auth-form-submit"
                disabled={!isComplete || loading || isExpired}
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </Button>
            </motion.div>

            {isExpired ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ marginTop: '1rem' }}
              >
                <button
                  type="button"
                  className="otp-resend-expired-btn"
                  onClick={handleResend}
                  disabled={resending || loading}
                >
                  {resending ? 'Sending...' : resendSuccess ? 'Code Sent!' : 'Resend OTP'}
                </button>
              </motion.div>
            ) : (
              <p className="otp-resend">
                Didn't receive the code?{' '}
                <button 
                  type="button" 
                  className="otp-resend-link"
                  onClick={handleResend}
                  disabled={resending || loading}
                >
                  {resending ? 'Sending...' : resendSuccess ? 'Code Sent!' : 'Resend'}
                </button>
              </p>
            )}
          </form>
        </Card>
      </motion.div>
    </AuthLayout>
  );
}

export default OTPVerification;
